import { addDays, daysBetween, isSunday } from "./date";
import type { CaloriePlanVersion, DayPlan, ISODate, ProgramVersion, TrainingBlock, TrainingPhase } from "./types";

export function activeProgram(programs: ProgramVersion[], date: ISODate): ProgramVersion {
  const active = programs
    .filter((program) => program.startsOn <= date && (!program.endsOn || program.endsOn >= date))
    .sort((a, b) => b.startsOn.localeCompare(a.startsOn))[0];
  if (!active) throw new Error(`Нет активной программы для ${date}`);
  return active;
}

export function weekNumber(program: ProgramVersion, date: ISODate): number {
  return Math.floor(daysBetween(program.startsOn, date) / 7) + 1;
}

export function phaseForDate(program: ProgramVersion, date: ISODate): TrainingPhase {
  const week = weekNumber(program, date);
  return program.phases.find((phase) => week >= phase.weekStart && week <= phase.weekEnd) ?? program.phases.at(-1)!;
}

export function calorieTarget(plan: CaloriePlanVersion, program: ProgramVersion, date: ISODate): number {
  const week = weekNumber(program, date);
  return plan.targets.find((target) => week >= target.weekStart && week <= target.weekEnd)?.kcal ?? plan.targets.at(-1)!.kcal;
}

export function buildDayPlan(programs: ProgramVersion[], caloriePlans: CaloriePlanVersion[], date: ISODate): DayPlan {
  const program = activeProgram(programs, date);
  const calories = caloriePlans.find((plan) => plan.startsOn <= date && (!plan.endsOn || plan.endsOn >= date)) ?? caloriePlans[0];
  const week = weekNumber(program, date);
  const phase = phaseForDate(program, date);
  const sunday = isSunday(date);
  const blocks = blocksForDate(program, phase, date);
  return {
    date,
    programDay: daysBetween(program.startsOn, date) + 1,
    weekNumber: week,
    phase,
    dayKind: "training",
    calorieTarget: calorieTarget(calories, program, date),
    blocks,
    nextTrainingLabel: sunday ? `${addDays(date, 1)} - ${phase.name}` : "Сегодня"
  };
}

export function blocksForDate(program: ProgramVersion, phase: TrainingPhase, date: ISODate): TrainingBlock[] {
  const warmup: TrainingBlock = {
    id: `${program.id}-warmup`,
    title: "Разминка",
    timeOfDay: "morning",
    exercises: program.warmup
  };
  return isSunday(date) ? [warmup, phase.morning, phase.sundayEvening] : [warmup, phase.morning, phase.evening];
}

export function cloneProgramAsFutureVersion(program: ProgramVersion, startsOn: ISODate, now: string): ProgramVersion {
  return {
    ...structuredClone(program),
    id: `program-${startsOn}-${crypto.randomUUID()}`,
    startsOn,
    endsOn: undefined,
    createdAt: now,
    updatedAt: now,
    revision: 1,
    name: `${program.name} - новая версия`
  };
}
